import React, { useState } from 'react';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import JoinInnerIcon from '@mui/icons-material/JoinInner';
import JoinFullIcon from '@mui/icons-material/JoinFull';
import BlockIcon from '@mui/icons-material/Block';
import { ConditionGroupType } from '../../../types/ruleset-types';

export interface GroupConfig {
  color: string;
  label: string;
  helperText: string;
}

interface ConditionGroupHeaderProps {
  groupType: ConditionGroupType;
  config: GroupConfig;
  isRoot: boolean;
  childCount: number;
  canAddMore: boolean;
  canAddNestedGroups: boolean;
  insideNot: boolean;
  disabled: boolean;
  onAddCondition: () => void;
  onAddGroup: (type: ConditionGroupType) => void;
  onTypeChange: (type: 'and' | 'or') => void;
  onRemove: () => void;
}

const ConditionGroupHeader: React.FC<ConditionGroupHeaderProps> = ({
  groupType,
  config,
  isRoot,
  childCount,
  canAddMore,
  canAddNestedGroups,
  insideNot,
  disabled,
  onAddCondition,
  onAddGroup,
  onTypeChange,
  onRemove,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleAddCondition = () => {
    handleCloseMenu();
    onAddCondition();
  };

  const handleAddGroup = (type: ConditionGroupType) => {
    handleCloseMenu();
    onAddGroup(type);
  };

  const showGroupToggle = (groupType === 'and' || groupType === 'or') && childCount >= 2;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      {showGroupToggle ? (
        <>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={groupType}
            onChange={(_, value) => value && onTypeChange(value)}
            disabled={disabled}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: '11px',
                px: 1.5,
                py: 0.25,
                border: '1px solid',
                borderColor: 'divider',
              },
            }}
          >
            <ToggleButton
              value="and"
              data-cy={isRoot ? 'btn-root-and' : 'btn-group-and'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(29, 154, 170, 0.12)',
                  borderColor: '#1D9AAA',
                  color: '#1D9AAA',
                  fontWeight: 600,
                },
              }}
            >
              AND
            </ToggleButton>
            <ToggleButton
              value="or"
              data-cy={isRoot ? 'btn-root-or' : 'btn-group-or'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(123, 31, 162, 0.12)',
                  borderColor: '#7b1fa2',
                  color: '#7b1fa2',
                  fontWeight: 600,
                },
              }}
            >
              OR
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '11px' }}>
            {config.helperText}
          </Typography>
        </>
      ) : isRoot && (groupType === 'and' || groupType === 'or') ? (
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '11px' }}>
          Match condition
        </Typography>
      ) : (
        <>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: config.color,
              textTransform: 'uppercase',
              fontSize: '11px',
              letterSpacing: '0.5px',
            }}
          >
            {config.label}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '11px' }}>
            {config.helperText}
          </Typography>
        </>
      )}

      <Box sx={{ flex: 1 }} />

      {canAddMore && (
        <>
          <IconButton
            size="small"
            onClick={handleOpenMenu}
            disabled={disabled}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: config.color,
                backgroundColor: `${config.color}10`,
              },
            }}
            data-cy="btn-add-condition"
          >
            <AddIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleAddCondition} data-cy="menu-add-condition">
              <ListItemIcon>
                <FilterAltOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Condition</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => handleAddGroup('and')}
              disabled={!canAddNestedGroups}
              data-cy="menu-add-and-group"
            >
              <ListItemIcon>
                <JoinInnerIcon
                  fontSize="small"
                  sx={{ color: canAddNestedGroups ? '#1D9AAA' : 'text.disabled' }}
                />
              </ListItemIcon>
              <ListItemText>AND Group</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => handleAddGroup('or')}
              disabled={!canAddNestedGroups}
              data-cy="menu-add-or-group"
            >
              <ListItemIcon>
                <JoinFullIcon
                  fontSize="small"
                  sx={{ color: canAddNestedGroups ? '#7b1fa2' : 'text.disabled' }}
                />
              </ListItemIcon>
              <ListItemText>OR Group</ListItemText>
            </MenuItem>
            {!insideNot && (
              <MenuItem
                onClick={() => handleAddGroup('not')}
                disabled={!canAddNestedGroups}
                data-cy="menu-add-not-group"
              >
                <ListItemIcon>
                  <BlockIcon
                    fontSize="small"
                    sx={{ color: canAddNestedGroups ? '#f57c00' : 'text.disabled' }}
                  />
                </ListItemIcon>
                <ListItemText>NOT Group</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      {!isRoot && (
        <Tooltip title="Remove group">
          <IconButton
            size="small"
            onClick={onRemove}
            disabled={disabled}
            sx={{
              color: 'text.disabled',
              '&:hover': {
                color: 'error.main',
                backgroundColor: 'error.lighter',
              },
            }}
            data-cy="btn-remove-group"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default ConditionGroupHeader;
